import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { getProductDetailSettings, saveProductDetailSettings } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';

interface ProductDetailSettings2Props {
  companyId: string | null;
  configId: string;
}

type GalleryTemplate = 'grid' | 'marketplace';

interface TacreoSettings {
  enabled: boolean;
  manuallySelected?: boolean;
  showFooter: boolean;
  showBottomNavbar: boolean;
  stickyInfoPanel: boolean;
  showRecentProducts: boolean;
  showSimilarProducts: boolean;
  showLegacyTabs: boolean;
  galleryTemplate: GalleryTemplate;
  enableHoverZoom: boolean;
  enableClickZoom: boolean;
  showGalleryArrows: boolean;
  gallerySlots: number;
  galleryRows: number;
  galleryColumns: number;
  galleryGapHorizontal: number;
  galleryGapVertical: number;
  priceFontFamily: string;
  priceFontSize: number;
  priceColor: string;
  addToCartButtonText: string;
  addToCartButtonColor: string;
  buyNowButtonText: string;
  defaultSizeLabel: string;
  detailDescriptionContent: string;
  paymentOptionsContent: string;
  shippingReturnsContent: string;
}

const defaultTacreoSettings: TacreoSettings = {
  enabled: false,
  manuallySelected: false,
  showFooter: false,
  showBottomNavbar: false,
  stickyInfoPanel: true,
  showRecentProducts: true,
  showSimilarProducts: true,
  showLegacyTabs: false,
  galleryTemplate: 'grid',
  enableHoverZoom: true,
  enableClickZoom: true,
  showGalleryArrows: true,
  gallerySlots: 4,
  galleryRows: 2,
  galleryColumns: 2,
  galleryGapHorizontal: 12,
  galleryGapVertical: 12,
  priceFontFamily: 'Roboto, sans-serif',
  priceFontSize: 26,
  priceColor: '#111111',
  addToCartButtonText: 'SEPETE EKLE',
  addToCartButtonColor: '#fb923c',
  buyNowButtonText: 'HEMEN AL',
  defaultSizeLabel: 'STANDART',
  detailDescriptionContent: '',
  paymentOptionsContent: '',
  shippingReturnsContent: '',
};

const defaultBasePayload: Record<string, any> = {
  enabled: true,
  imageGalleryPosition: 'left',
  showImageThumbnails: true,
  showImageZoom: true,
  imageGalleryStyle: 'slider',
  showSku: true,
  showBrand: true,
  showCategory: false,
  showStock: true,
  showRating: true,
  showDescription: true,
  showSpecifications: true,
  showReviews: true,
  showOriginalPrice: true,
  showDiscountPercentage: true,
  showTaxInfo: true,
  currencySymbol: '₺',
  priceFormat: 'after',
  showQuantitySelector: true,
  showAddToCart: true,
  showBuyNow: true,
  showWishlist: true,
  showShare: true,
  containerMaxWidth: 1200,
  imageHeight: 500,
  buttonStyle: 'default',
  colorScheme: 'light',
  addToCartButtonColor: '#f97316',
  buyNowButtonColor: '#2563eb',
  enableBreadcrumb: true,
  enableRecommendations: true,
  enableRecentlyViewed: false,
  enableSocialProof: true,
  servicesJson: JSON.stringify({}),
};

const ProductDetailSettings2: React.FC<ProductDetailSettings2Props> = ({ companyId, configId }) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<TacreoSettings>(defaultTacreoSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [paymentContentExpanded, setPaymentContentExpanded] = useState(false);
  const [shippingContentExpanded, setShippingContentExpanded] = useState(false);
  const basePayloadRef = useRef<Record<string, any>>(defaultBasePayload);
  const servicesDataRef = useRef<Record<string, any>>({});
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!companyId || !configId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await getProductDetailSettings(companyId, configId);
        if (res?.success && res.data) {
          setSettingsId(res.data.id || null);
          basePayloadRef.current = { ...defaultBasePayload, ...res.data };
          let servicesObj: Record<string, any> = {};
          if (res.data.servicesJson) {
            try {
              servicesObj = JSON.parse(res.data.servicesJson);
            } catch {
              servicesObj = {};
            }
          }
          servicesDataRef.current = servicesObj;
          setSettings({
            ...defaultTacreoSettings,
            ...(servicesObj.tacreoSettings || {}),
          });
        } else {
          basePayloadRef.current = { ...defaultBasePayload };
          servicesDataRef.current = {};
          setSettings(defaultTacreoSettings);
        }
      } catch (e: any) {
        setError(e?.message || 'Tacreo ürün detay ayarları yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyId, configId]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const queueSave = (nextSettings: TacreoSettings) => {
    if (!companyId || !configId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        const mergedServices = {
          ...servicesDataRef.current,
          tacreoSettings: nextSettings,
        };
        servicesDataRef.current = mergedServices;
        const normalizedBasePayload = {
          ...basePayloadRef.current,
          // Ürün Detayları 2 açıldığında Ürün Detayları 1'i otomatik kapat.
          enabled: nextSettings.enabled ? false : basePayloadRef.current.enabled,
        };
        basePayloadRef.current = normalizedBasePayload;
        const payload = {
          ...normalizedBasePayload,
          id: settingsId || basePayloadRef.current.id || null,
          companyId,
          configId,
          servicesJson: JSON.stringify(mergedServices),
        };
        const res = await saveProductDetailSettings(payload);
        if (res?.success && res?.data?.id && !settingsId) {
          setSettingsId(res.data.id);
        }
      } catch (e) {
        console.error('Tacreo ürün detay ayarları kaydetme hatası:', e);
      } finally {
        setSaving(false);
      }
    }, 700);
  };

  const updateSetting = <K extends keyof TacreoSettings>(key: K, value: TacreoSettings[K]) => {
    const next = {
      ...settings,
      [key]: value,
      ...(key === 'enabled' ? { manuallySelected: true } : {}),
    };
    setSettings(next);
    queueSave(next);
  };

  const resetDefaults = () => {
    setSettings(defaultTacreoSettings);
    queueSave(defaultTacreoSettings);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            {t('ecommerceProductDetail2.title')}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={resetDefaults}>
          {t('ecommerceProductDetail2.actions.resetToDefault')}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('ecommerceProductDetail2.info')}
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saving && <Alert severity="success" sx={{ mb: 2 }}>{t('ecommerceProductDetail2.saving')}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('ecommerceProductDetail2.sections.appearance')}</Typography>
              <FormControlLabel control={<Switch checked={settings.enabled} onChange={(e) => updateSetting('enabled', e.target.checked)} />} label={t('ecommerceProductDetail2.fields.layoutEnabled')} />
              <FormControlLabel control={<Switch checked={settings.stickyInfoPanel} onChange={(e) => updateSetting('stickyInfoPanel', e.target.checked)} />} label={t('ecommerceProductDetail2.fields.stickyRightPanel')} />
              <FormControlLabel control={<Switch checked={settings.showLegacyTabs} onChange={(e) => updateSetting('showLegacyTabs', e.target.checked)} />} label={t('ecommerceProductDetail2.fields.showLegacyTabs')} />
              <FormControlLabel control={<Switch checked={settings.showRecentProducts} onChange={(e) => updateSetting('showRecentProducts', e.target.checked)} />} label={t('ecommerceProductDetail2.fields.showRecentProducts')} />
              <FormControlLabel control={<Switch checked={settings.showSimilarProducts} onChange={(e) => updateSetting('showSimilarProducts', e.target.checked)} />} label={t('ecommerceProductDetail2.fields.showSimilarProducts')} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('ecommerceProductDetail2.sections.pageComponents')}</Typography>
              <FormControlLabel control={<Switch checked={settings.showFooter} onChange={(e) => updateSetting('showFooter', e.target.checked)} />} label={t('ecommerceProductDetail2.fields.showFooter')} />
              <FormControlLabel control={<Switch checked={settings.showBottomNavbar} onChange={(e) => updateSetting('showBottomNavbar', e.target.checked)} />} label={t('ecommerceProductDetail2.fields.showBottomNavbar')} />

              <Box sx={{ mt: 2, mb: 1 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ typography: 'body2', color: 'text.primary', mb: 0.5 }}>
                    {t('ecommerceProductDetail2.fields.galleryTemplate')}
                  </FormLabel>
                  <RadioGroup
                    value={settings.galleryTemplate || 'grid'}
                    onChange={(e) => updateSetting('galleryTemplate', e.target.value as GalleryTemplate)}
                  >
                    <FormControlLabel
                      value="grid"
                      control={<Radio size="small" />}
                      label={t('ecommerceProductDetail2.fields.galleryTemplateGrid')}
                    />
                    <FormControlLabel
                      value="marketplace"
                      control={<Radio size="small" />}
                      label={t('ecommerceProductDetail2.fields.galleryTemplateMarketplace')}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {(settings.galleryTemplate || 'grid') === 'marketplace' ? (
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={<Switch checked={settings.enableHoverZoom !== false} onChange={(e) => updateSetting('enableHoverZoom', e.target.checked)} />}
                    label={t('ecommerceProductDetail2.fields.enableHoverZoom')}
                  />
                  <FormControlLabel
                    control={<Switch checked={settings.enableClickZoom !== false} onChange={(e) => updateSetting('enableClickZoom', e.target.checked)} />}
                    label={t('ecommerceProductDetail2.fields.enableClickZoom')}
                  />
                  <FormControlLabel
                    control={<Switch checked={settings.showGalleryArrows !== false} onChange={(e) => updateSetting('showGalleryArrows', e.target.checked)} />}
                    label={t('ecommerceProductDetail2.fields.showGalleryArrows')}
                  />
                </Box>
              ) : (
                <>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">{t('ecommerceProductDetail2.fields.gallerySlots', { count: settings.gallerySlots })}</Typography>
                    <Slider
                      min={2}
                      max={12}
                      step={1}
                      marks
                      value={settings.gallerySlots}
                      onChange={(_, v) => updateSetting('gallerySlots', v as number)}
                    />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">{t('ecommerceProductDetail2.fields.galleryRows', { count: settings.galleryRows })}</Typography>
                    <Slider
                      min={1}
                      max={4}
                      step={1}
                      marks
                      value={settings.galleryRows}
                      onChange={(_, v) => updateSetting('galleryRows', v as number)}
                    />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">{t('ecommerceProductDetail2.fields.galleryColumns', { count: settings.galleryColumns })}</Typography>
                    <Slider
                      min={1}
                      max={4}
                      step={1}
                      marks
                      value={settings.galleryColumns}
                      onChange={(_, v) => updateSetting('galleryColumns', v as number)}
                    />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">{t('ecommerceProductDetail2.fields.horizontalGap', { count: settings.galleryGapHorizontal })}</Typography>
                    <Slider
                      min={0}
                      max={40}
                      step={1}
                      value={settings.galleryGapHorizontal}
                      onChange={(_, v) => updateSetting('galleryGapHorizontal', v as number)}
                    />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">{t('ecommerceProductDetail2.fields.verticalGap', { count: settings.galleryGapVertical })}</Typography>
                    <Slider
                      min={0}
                      max={40}
                      step={1}
                      value={settings.galleryGapVertical}
                      onChange={(_, v) => updateSetting('galleryGapVertical', v as number)}
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('ecommerceProductDetail2.sections.addToCartButton')}</Typography>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceProductDetail2.fields.buttonText')}
                value={settings.addToCartButtonText}
                onChange={(e) => updateSetting('addToCartButtonText', e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceProductDetail2.fields.buttonColor')}
                value={settings.addToCartButtonColor}
                onChange={(e) => updateSetting('addToCartButtonColor', e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('ecommerceProductDetail2.sections.buyNowButton')}</Typography>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceProductDetail2.fields.buttonText')}
                value={settings.buyNowButtonText}
                onChange={(e) => updateSetting('buyNowButtonText', e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('ecommerceProductDetail2.sections.sizeArea')}</Typography>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceProductDetail2.fields.defaultSizeText')}
                helperText={t('ecommerceProductDetail2.fields.defaultSizeHelp')}
                value={settings.defaultSizeLabel}
                onChange={(e) => updateSetting('defaultSizeLabel', e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('ecommerceProductDetail2.sections.rightPanelContent')}</Typography>
              <Accordion
                disableGutters
                elevation={0}
                expanded={paymentContentExpanded}
                onChange={(_, expanded) => setPaymentContentExpanded(expanded)}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography fontWeight={600}>{t('ecommerceProductDetail2.fields.paymentOptionsContent')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {settings.paymentOptionsContent?.length || 0} karakter
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={12}
                    value={settings.paymentOptionsContent}
                    onChange={(e) => updateSetting('paymentOptionsContent', e.target.value)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters
                elevation={0}
                expanded={shippingContentExpanded}
                onChange={(_, expanded) => setShippingContentExpanded(expanded)}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography fontWeight={600}>{t('ecommerceProductDetail2.fields.shippingReturnsContent')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {settings.shippingReturnsContent?.length || 0} karakter
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={12}
                    value={settings.shippingReturnsContent}
                    onChange={(e) => updateSetting('shippingReturnsContent', e.target.value)}
                  />
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('ecommerceProductDetail2.sections.priceTextStyle')}</Typography>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceProductDetail2.fields.priceFontFamily')}
                value={settings.priceFontFamily}
                onChange={(e) => updateSetting('priceFontFamily', e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">{t('ecommerceProductDetail2.fields.priceFontSize', { count: settings.priceFontSize })}</Typography>
                <Slider
                  min={10}
                  max={48}
                  step={1}
                  value={settings.priceFontSize}
                  onChange={(_, v) => updateSetting('priceFontSize', v as number)}
                />
              </Box>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceProductDetail2.fields.priceColor')}
                value={settings.priceColor}
                onChange={(e) => updateSetting('priceColor', e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ProductDetailSettings2;
